'use client';

import { c as _c } from "react/compiler-runtime";
import { type FundingOperationDetail, type FundingQuotedAmounts, formatFiatUnits, fundingOperationAmounts, fundingOperationPresentation, fundingReceivedPresentation } from '@morfi/onramp-sdk';
import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedButton } from '@/components/ui/animated-button';
import { CircleIcon, Loading03Icon, Tick02Icon } from '@/components/ui/icon/glyphs';
import { Icon } from '@/components/ui/icon/index';
export function FundingOperationProgress(t0) {
  const $ = _c(66);
  const {
    operation,
    quoted,
    onReceipt,
    onTryAnotherMethod
  } = t0;
  const t = useTranslations("onramp");
  const locale = useLocale();
  let t1;
  if ($[0] !== operation) {
    t1 = fundingOperationPresentation(operation);
    $[0] = operation;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    phase,
    timeline,
    stages
  } = t1;
  const complete = phase === "complete";
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[2] !== complete || $[3] !== locale || $[4] !== operation || $[5] !== phase || $[6] !== quoted || $[7] !== stages || $[8] !== t || $[9] !== timeline) {
    const amounts = fundingOperationAmounts(operation, quoted);
    const format = units => units === null ? t("amount_pending") : "exchangeSource" in operation || "cryptoDepositSource" in operation || "connectedWalletSource" in operation ? t("exchange_fees_unknown") : formatFiatUnits(units, operation.fiatDecimals, operation.fiatCurrency, locale);
    const receiveValue = fundingReceivedPresentation(operation, locale, quoted);
    const received = phase === "sandbox_complete" ? t("receipt_delivery_sandbox") : receiveValue.value ?? t("amount_pending");
    const t6 = operation.purchase ? "direct_purchase_pending" : "progress_processing_title";
    let t7;
    if ($[14] !== t || $[15] !== t6) {
      t7 = t(t6);
      $[14] = t;
      $[15] = t6;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    let t8;
    if ($[17] !== operation.purchase || $[18] !== t) {
      t8 = operation.purchase ? t("direct_purchase_received") : t("progress_complete_title");
      $[17] = operation.purchase;
      $[18] = t;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    let t9;
    if ($[20] !== t) {
      t9 = t("progress_sandbox_complete_title");
      $[20] = t;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    let t10;
    if ($[22] !== t) {
      t10 = t("progress_processing_title");
      $[22] = t;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    let t11;
    if ($[24] !== t) {
      t11 = t("progress_failed_title");
      $[24] = t;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    let t12;
    if ($[26] !== t) {
      t12 = t("progress_cancelled_title");
      $[26] = t;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    let t13;
    if ($[28] !== t) {
      t13 = t("progress_refunded_title");
      $[28] = t;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    let t14;
    if ($[30] !== t10 || $[31] !== t11 || $[32] !== t12 || $[33] !== t13 || $[34] !== t7 || $[35] !== t8 || $[36] !== t9) {
      t14 = {
        processing: t7,
        complete: t8,
        sandbox_complete: t9,
        unknown: t10,
        failed: t11,
        cancelled: t12,
        refunded: t13
      };
      $[30] = t10;
      $[31] = t11;
      $[32] = t12;
      $[33] = t13;
      $[34] = t7;
      $[35] = t8;
      $[36] = t9;
      $[37] = t14;
    } else {
      t14 = $[37];
    }
    const titles = t14;
    const descriptions = {
      processing: operation.status === "delivered" ? operation.walletDelivery?.state === "sandbox_confirmed" ? t("wallet_delivery_sandbox_confirmed") : t("wallet_delivery_unconfirmed") : t("progress_processing_body"),
      complete: t("progress_complete_body", {
        amount: received
      }),
      sandbox_complete: t("wallet_delivery_sandbox_confirmed"),
      unknown: t("progress_unknown_body"),
      failed: t("progress_failed_body"),
      cancelled: t("progress_cancelled_body"),
      refunded: t("progress_refunded_body")
    };
    const art = complete || phase === "sandbox_complete" ? "success" : phase === "failed" || phase === "cancelled" ? "declined" : phase === "refunded" ? "refund" : phase === "unknown" ? "unknown" : "processing";
    const steps = [[t("progress_started"), stages[0] !== "complete" ? t("progress_unknown_body") : "exchangeSource" in operation || "cryptoDepositSource" in operation || "connectedWalletSource" in operation || operation.chargedUnits === null ? t("progress_authorized") : `${format(amounts.chargedUnits)} · ${t("progress_authorized")}`], [t("progress_processing"), stages[1] === "complete" ? t("progress_processed") : t("progress_waiting")], [t("progress_delivered"), stages[2] === "complete" ? phase === "sandbox_complete" ? t("receipt_delivery_sandbox") : t("progress_wallet_amount", {
      amount: received
    }) : t("progress_awaiting_delivery")]];
    t2 = "flex flex-col gap-4.5 pt-2";
    t3 = "polite";
    const t15 = operation.purchase?.imageNsfw ?? false;
    const t16 = operation.purchase?.imageUrl || `/assets/onramp/status-${art}.png`;
    let t17;
    if ($[38] !== t15 || $[39] !== t16) {
      t17 = <OptimizedImage nsfw={t15} variant="thumbnail" preview={false} loadReveal="none" rounded="" imageClassName="object-contain" src={t16} alt="" size="size-18" className="shrink-0 bg-transparent" />;
      $[38] = t15;
      $[39] = t16;
      $[40] = t17;
    } else {
      t17 = $[40];
    }
    const t18 = titles[phase];
    let t19;
    if ($[41] !== t18) {
      t19 = <h3 className="text-lg font-semibold leading-6">{t18}</h3>;
      $[41] = t18;
      $[42] = t19;
    } else {
      t19 = $[42];
    }
    let t20;
    if ($[43] !== operation.purchase) {
      t20 = operation.purchase && <p className="text-body">{operation.purchase.title}</p>;
      $[43] = operation.purchase;
      $[44] = t20;
    } else {
      t20 = $[44];
    }
    const t21 = descriptions[phase];
    let t22;
    if ($[45] !== t21) {
      t22 = <p className="mt-1.5 text-caption leading-4.5 text-foreground-primary">{t21}</p>;
      $[45] = t21;
      $[46] = t22;
    } else {
      t22 = $[46];
    }
    let t23;
    if ($[47] !== t19 || $[48] !== t20 || $[49] !== t22) {
      t23 = <div>{t19}{t20}{t22}</div>;
      $[47] = t19;
      $[48] = t20;
      $[49] = t22;
      $[50] = t23;
    } else {
      t23 = $[50];
    }
    if ($[51] !== t17 || $[52] !== t23) {
      t4 = <div className="flex items-center gap-4">{t17}{t23}</div>;
      $[51] = t17;
      $[52] = t23;
      $[53] = t4;
    } else {
      t4 = $[53];
    }
    t5 = timeline ? <><ol>{steps.map((t24, index) => {
          const [title, description] = t24;
          return <li key={title} className="flex items-start gap-3 border-b border-border-primary py-4 text-body last:border-0"><span aria-hidden="true" className={`grid size-7 shrink-0 place-items-center rounded-full ${stages[index] === "complete" ? "bg-transparent text-feedback-success-content" : stages[index] === "loading" ? "bg-background-brand text-foreground-brand" : "bg-background-tertiary text-foreground-primary"}`}><Icon icon={stages[index] === "complete" ? Tick02Icon : stages[index] === "loading" ? Loading03Icon : CircleIcon} className={stages[index] === "loading" ? "size-5 animate-spin motion-reduce:animate-none" : "size-5"} /></span><div><strong className="text-body font-medium">{title}</strong><p className="text-caption leading-4.5 text-foreground-primary">{description}</p></div><small className="ml-auto whitespace-nowrap text-micro leading-5 text-foreground-primary">{stages[index] === "complete" ? t("progress_confirmed") : stages[index] === "loading" ? t("progress_in_progress") : t("progress_pending")}</small></li>;
        })}</ol><dl className="border-t border-border-primary">{[[t("progress_you_pay"), format(amounts.chargedUnits)], [t("fees"), format(amounts.feeUnits)], [receiveValue.estimated ? t("estimated_receive") : t("progress_you_receive"), received]].map(_temp)}</dl></> : null;
    $[2] = complete;
    $[3] = locale;
    $[4] = operation;
    $[5] = phase;
    $[6] = quoted;
    $[7] = stages;
    $[8] = t;
    $[9] = timeline;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
  }
  let t6;
  if ($[54] !== onReceipt || $[55] !== onTryAnotherMethod || $[56] !== operation.status || $[57] !== phase || $[58] !== t) {
    t6 = phase !== "unknown" && (phase !== "processing" || operation.status === "delivered") ? <AnimatedButton className="h-11 rounded-control" onClick={phase === "failed" ? onTryAnotherMethod : onReceipt}>{phase === "failed" ? t("progress_try_another_method") : t("progress_view_receipt")}</AnimatedButton> : null;
    $[54] = onReceipt;
    $[55] = onTryAnotherMethod;
    $[56] = operation.status;
    $[57] = phase;
    $[58] = t;
    $[59] = t6;
  } else {
    t6 = $[59];
  }
  let t7;
  if ($[60] !== t2 || $[61] !== t3 || $[62] !== t4 || $[63] !== t5 || $[64] !== t6) {
    t7 = <div className={t2} aria-live={t3}>{t4}{t5}{t6}</div>;
    $[60] = t2;
    $[61] = t3;
    $[62] = t4;
    $[63] = t5;
    $[64] = t6;
    $[65] = t7;
  } else {
    t7 = $[65];
  }
  return t7;
}
function _temp(t0) {
  const [label, value] = t0;
  return <div key={label} className="flex justify-between gap-4 border-b border-border-primary py-3 text-body"><dt>{label}</dt><dd className="font-medium">{value}</dd></div>;
}